import React, { useState } from 'react'
import CardInfo from '../../../components/CardInfo/CardInfo'
import PropTypes from 'prop-types'
import Trash from '../../../svg/Trash'
import ServicingBranchModal from './ServicingBranchModal'
import UpdateAssetNameModal from './UpdateAssetNameModal'
import useIntercom from '../../../hooks/useIntercom'
import clsx from 'clsx'
import { useNetworkStatus } from '../../../utils/networkStatus'
import ButtonWithTooltip from '../../../components/ButtonWithTooltip/ButtonWithTooltip'

const DetailCol = ({ children }) => {
    return (
        <div className="flex flex-col items-start justify-between md:gap-y-6 gap-y-0 md:w-auto w-full">
            {children}
        </div>
    )
}

DetailCol.propTypes = {
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.element]),
}

const Detail = ({ field, value, isGray = false }) => {
    return (
        <div
            className={clsx(
                'flex md:flex-col flex-row md:items-start items-center md:justify-start justify-between gap-y-1 md:w-auto w-full md:p-0 px-4 md:py-0 py-4.5',
                isGray && 'md:bg-transparent bg-pageBackground',
            )}
        >
            <p className="md:text-textDarkerGray text-textDarkGray font-medium text-sm">
                {field || '-'}
            </p>
            <div className="md:text-sm text-base font-normal md:text-textDarkGray text-textDarkerGray">
                {value || '-'}
            </div>
        </div>
    )
}

Detail.propTypes = {
    field: PropTypes.string,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element,
        PropTypes.any,
    ]),
    isGray: PropTypes.bool,
}

const ServiceBranchText = ({ servicingBranch }) => {
    const [modalSBOpen, setModalSBOpen] = useState(false)

    return (
        <>
            <ServicingBranchModal
                isOpen={modalSBOpen}
                setIsOpen={setModalSBOpen}
                servicingBranch={servicingBranch}
            />
            <span
                onClick={() => setModalSBOpen(true)}
                className="hover:underline text-links font-medium text-sm cursor-pointer"
            >
                {servicingBranch.name}
            </span>
        </>
    )
}

ServiceBranchText.propTypes = {
    servicingBranch: PropTypes.oneOfType([PropTypes.object, PropTypes.any]),
}

const AssetGeneralDetails = props => {
    const { assetInfo, ...otherProps } = props
    const [updateAssetNameModalOpen, setUpdateAssetModalOpen] = useState(false)
    const { intercomSendMessage } = useIntercom()
    const { isOffline } = useNetworkStatus()

    const lastHourReading = assetInfo?.latestHourReading
        ? assetInfo?.latestHourReading +
          'h on ' +
          assetInfo?.latestHourReadingDate
        : null

    const renderContentGeneralDetails = () => {
        return (
            <div className="w-full h-full flex md:flex-row flex-col items-start justify-between md:px-6 md:py-4">
                <DetailCol>
                    <Detail
                        field="Make"
                        value={
                            assetInfo?.manufacturer != 'Other'
                                ? `${assetInfo?.manufacturer} ${assetInfo?.simpleType}`
                                : assetInfo?.simpleType
                        }
                        isGray
                    />
                    <Detail
                        field="Servicing Branch"
                        value={
                            assetInfo?.serviceBranch && (
                                <ServiceBranchText
                                    servicingBranch={assetInfo?.serviceBranch}
                                />
                            )
                        }
                    />
                </DetailCol>

                <DetailCol>
                    <Detail isGray field="Model" value={assetInfo?.model} />
                    <Detail field="Ownership" value={assetInfo?.categoryType} />
                </DetailCol>

                <DetailCol>
                    <Detail
                        field="Serial Number"
                        value={assetInfo?.serialLotNumber}
                        isGray
                    />
                    <Detail
                        field="Latest Hour Meter Reading"
                        value={lastHourReading}
                    />
                </DetailCol>

                <DetailCol>
                    <Detail
                        field="Equipment Number"
                        value={assetInfo?.equipmentNumber}
                        isGray
                    />
                    <Detail
                        field="Acquisition Date"
                        value={assetInfo?.sapAcquisitionDate}
                    />
                </DetailCol>
            </div>
        )
    }

    const onDeleteFleet = () => {
        const text =
            'The asset is no longer in my fleet: ' + assetInfo?.equipmentNumber
        intercomSendMessage(text)
    }

    return (
        <>
            <UpdateAssetNameModal
                isOpen={updateAssetNameModalOpen}
                setIsOpen={setUpdateAssetModalOpen}
                assetCustomerAlias={
                    assetInfo?.customerAlias
                        ? assetInfo?.customerAlias
                        : assetInfo?.name
                }
                {...otherProps}
            />
            <CardInfo
                title="General Details"
                content={renderContentGeneralDetails()}
                titleClass="md:w-auto w-full"
                hasPaddingContent={false}
                headerActions={[
                    <ButtonWithTooltip
                        key="updateAssetName"
                        text="Update Customer Unit Number"
                        variant="tertiary"
                        onClick={() => setUpdateAssetModalOpen(true)}
                        type="button"
                        className="md:w-auto md:flex-grow-0 flex-grow w-full h-10"
                        disabled={isOffline}
                        isOffline={isOffline}
                    />,
                    <ButtonWithTooltip
                        onClick={onDeleteFleet}
                        type="button"
                        key="deleteAsset"
                        text=""
                        variant="tertiary"
                        icon={<Trash className="w-5 h-5 mr-0" />}
                        className="w-10 h-10 p-0 rounded-lg"
                        disabled={isOffline}
                        isOffline={isOffline}
                        width="auto"
                    />,
                ]}
            />
        </>
    )
}

AssetGeneralDetails.propTypes = {
    assetInfo: PropTypes.oneOfType([PropTypes.object, PropTypes.any]),
}

export default AssetGeneralDetails
